import { Card } from "primereact/card";
import {
  CustomAction,
  getAddressColumns,
  SovColumnProps,
} from "./AddressCardColumns";
import * as client from "../../../client";
import { formatExactMoneyDollars } from "../Money";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import styles from "./AddressCard.module.css";
import {
  CONSTRUCTION_MODAL_INDEX,
  EditSovModal,
  OCCUPANCY_MODAL_INDEX,
} from "./EditSovModal";
import { ColumnMappingsModal } from "./ColumnMappingsModal";
import {
  useLocalUser,
  useThisReportIsUpdating,
  useUpdateAddresses,
  useUpdateReport,
} from "../data";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../routes";
import {
  applyDeltasAsOverride,
  applyOverridesToAddress,
  applyOverridesToResponse,
} from "../Conversion";
import { ThemeContext } from "../../../themes/Theme";
import { Tooltip } from "primereact/tooltip";
import { flagTextFor, maxSeverityForAddressFlags } from "./flags";
import { groupBy, isEqual } from "lodash";
import { BulkEditModal, BulkEditModalState } from "./BulkEditModal";
import { StreetViewModal, StreetViewModalState } from "./StreetViewModal";
import { flaggedHighlightColor } from "../dashboard/util";
import { v4 as uuid } from "uuid";
import { useLogUIEventCallback } from "../../../amplitude";
import { EditSheets } from "./EditSheets";
import { ReconciliationFunctionModal } from "./ReconciliationFunctionModal";
import { isRequiredField } from "./required";
import { confirmDialog } from "primereact/confirmdialog";
import { ClientId, featureEnabled } from "../../../client-features";
import { useClientId } from "../../../ClientConfig";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { useRerunningMessage } from "../RerunUI";
import { RawAddressWithIndices } from "./types";
import { ColumnEditModal } from "./ColumnEditModal";
import { AddAddressModal } from "./AddAddressModal";
import { SetFilter } from "./SetFilter";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ColDef,
  CellEditingStoppedEvent,
  IHeaderParams,
  themeQuartz,
  colorSchemeDark,
  colorSchemeLight,
} from "ag-grid-community";

const COVERAGE_FIELDS: Set<keyof client.RawAddress> = new Set([
  "tiv",
  "building_value",
  "bpp_value",
  "bi_incl_ee_value",
  "bi_excl_ee_value",
  "tenant_betterments_value",
  "signs_value",
  "furniture_and_fixtures_value",
  "inventory_value",
  "contractors_equipment_value",
  "outdoor_property_and_autos_value",
]);

const naturalSortComparator = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  const strA = String(a);
  const strB = String(b);

  // Split into segments of text and numbers
  const segmentRegex = /(\d+|\D+)/g;
  const partsA = strA.match(segmentRegex) ?? [];
  const partsB = strB.match(segmentRegex) ?? [];

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    if (i >= partsA.length) return -1;
    if (i >= partsB.length) return 1;

    const partA = partsA[i];
    const partB = partsB[i];

    const numA = Number(partA);
    const numB = Number(partB);
    const bothNumeric = !isNaN(numA) && !isNaN(numB);

    if (bothNumeric) {
      if (numA !== numB) return numA - numB;
    } else {
      const cmp = partA.localeCompare(partB);
      if (cmp !== 0) return cmp;
    }
  }

  return 0;
};

const computeFilteredTotal = (
  api: IHeaderParams["api"],
  field: string
): number => {
  let total = 0;
  api.forEachNodeAfterFilter((node) => {
    if (!node.data) return;
    const val = (node.data as Record<string, unknown>)[field];
    if (val != null && !isNaN(Number(val))) {
      total += Number(val);
    }
  });
  return total;
};

const SovHeader = (
  params: IHeaderParams & {
    columnInfo: SovColumnProps;
    isSortable: boolean;
    onBulkEdit: (columnInfo: SovColumnProps) => void;
  }
) => {
  const { columnInfo, isSortable, onBulkEdit } = params;

  const field = columnInfo.field;
  const isCoverageField = !!field && COVERAGE_FIELDS.has(field);

  const [sortDirection, setSortDirection] = useState(params.column.getSort());

  const [filterActive, setFilterActive] = useState(
    params.column.isFilterActive()
  );

  useEffect(() => {
    const sortListener = () => setSortDirection(params.column.getSort());
    params.column.addEventListener("sortChanged", sortListener);
    return () => params.column.removeEventListener("sortChanged", sortListener);
  }, [params.column]);

  useEffect(() => {
    const filterListener = () =>
      setFilterActive(params.column.isFilterActive());
    params.column.addEventListener("filterActiveChanged", filterListener);
    return () =>
      params.column.removeEventListener("filterActiveChanged", filterListener);
  }, [params.column]);

  const handleSortClick = () => {
    if (isSortable) {
      params.progressSort(false);
    }
  };

  // Compute the header total directly from the grid's filtered rows.
  let headerTotalValue: string | undefined;
  if (isCoverageField && field) {
    const total = computeFilteredTotal(params.api, field);
    if (total !== 0) {
      headerTotalValue = formatExactMoneyDollars(total, {
        withRounding: true,
        withDollarSign: true,
      });
    }
  }
  const hasHeaderValue = !!headerTotalValue;

  return (
    <div
      className={styles.headerWithTotal}
      onClick={handleSortClick}
      style={{ cursor: isSortable ? "pointer" : "default" }}
    >
      <div className={styles.header}>
        {columnInfo.bulkEdit ? (
          <div
            className={styles.bulkEditColumnHeader}
            onClick={(e) => {
              e.stopPropagation();
              onBulkEdit(columnInfo);
            }}
          >
            {columnInfo.columnName}{" "}
          </div>
        ) : (
          <>{columnInfo.columnName} </>
        )}
        {columnInfo.infoText && (
          <TooltipWrapper tooltipText={columnInfo.infoText} maxWidth={400}>
            <i className="pi pi-info-circle" style={{ fontSize: "12px" }} />
          </TooltipWrapper>
        )}
        {isSortable && sortDirection && (
          <i
            className={
              sortDirection === "asc"
                ? "pi pi-sort-amount-up-alt"
                : "pi pi-sort-amount-down"
            }
            style={{ fontSize: "12px", marginLeft: "2px" }}
          />
        )}
        {columnInfo.filterable && (
          <i
            className={filterActive ? "pi pi-filter-fill" : "pi pi-filter"}
            style={{
              fontSize: "12px",
              marginLeft: "2px",
              cursor: "pointer",
              color: filterActive ? "var(--primary-color)" : undefined,
            }}
            onClick={(e) => {
              e.stopPropagation();
              params.showFilter(e.currentTarget);
            }}
          />
        )}
      </div>
      {hasHeaderValue && (
        <div style={{ color: "var(--primary-color)" }}>{headerTotalValue}</div>
      )}
    </div>
  );
};

type AddressCardProps = {
  specialtyPropertyInfo: client.ReportResponse;
  setMapVisible?: (visible: boolean) => void;
};

const configForAddressAndColumnInfo = (
  clientId: ClientId,
  address: client.Address,
  columnInfo: SovColumnProps,
  isLightMode: boolean
) => {
  const rawAddress = applyOverridesToAddress(address);
  const value = columnInfo.getValue(rawAddress);
  const hasOverride =
    !!columnInfo.field && !!address.private_overrides[columnInfo.field];
  const maxSeverity = maxSeverityForAddressFlags(address, columnInfo.field);
  const isBlank = (value ?? "") === "";
  const isRequiredAndIsBlank =
    isBlank && isRequiredField(clientId, columnInfo.field);
  const flagText = flagTextFor(
    address,
    columnInfo.field,
    columnInfo.showTooltipsWhenNotFlagged,
    isRequiredAndIsBlank
  );
  const hasHighlight = maxSeverity || hasOverride || isRequiredAndIsBlank;

  return {
    originalValue: value,
    flagText: flagText,
    hasHighlight: hasHighlight,
    highlightColor: flaggedHighlightColor(
      maxSeverity,
      isLightMode,
      hasOverride,
      isRequiredAndIsBlank
    ),
    isBlank: isBlank,
  };
};

type CellBodyProps = {
  columnInfo: SovColumnProps;
  fullAddress: client.Address;
  isLightMode: boolean;
  isUpdatingAddress: boolean;
  isDisabled: boolean;
  onClick?: () => void;
  tooltipOverride?: string;
};

const CellBody: React.FC<CellBodyProps> = ({
  columnInfo,
  fullAddress,
  isLightMode,
  isUpdatingAddress,
  isDisabled,
  onClick,
  tooltipOverride,
}) => {
  const isUpdating = isUpdatingAddress;
  const clientId = useClientId();
  const { originalValue, flagText, hasHighlight, highlightColor, isBlank } =
    configForAddressAndColumnInfo(
      clientId,
      fullAddress,
      columnInfo,
      isLightMode
    );
  let value = originalValue;
  if (typeof value === "number") {
    value = formatExactMoneyDollars(value, {
      withRounding: true,
      withDollarSign: true,
    });
  } else if (columnInfo.renderNumberWithCommas && value) {
    const num = Number(value);
    if (!isNaN(num)) {
      value = num.toLocaleString();
    }
  }

  const tooltipId = `tooltip-${uuid()}`;

  return (
    <div
      className={styles.cellTextWrapper}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "unset" }}
    >
      <Tooltip
        target={`#${tooltipId}`}
        position="left"
        style={{ maxWidth: "600px" }}
      />
      <span
        className={styles.cellText}
        id={tooltipId}
        style={
          hasHighlight
            ? {
                backgroundColor: highlightColor,
                padding: "2px",
                opacity: isUpdating || isDisabled ? "0.5" : "1.0",
                width: isBlank ? "40px" : "auto",
                height: isBlank ? "23px" : "auto",
              }
            : {
                opacity: isUpdating || isDisabled ? "0.5" : "1.0",
              }
        }
        data-pr-tooltip={tooltipOverride ?? flagText}
        data-pr-showdelay={500}
      >
        {value && columnInfo.isIconValue ? <span className={value} /> : value}
      </span>
    </div>
  );
};

// AG Grid theme configuration
const agThemeLight = themeQuartz.withPart(colorSchemeLight);
const agThemeDark = themeQuartz.withPart(colorSchemeDark);

// AG Grid cells have ~24px of internal padding that PrimeReact columns didn't have.
// Add this to each column width so text doesn't get clipped.
const AG_GRID_COL_PADDING = 24;

export const AddressCard: React.FC<AddressCardProps> = ({
  specialtyPropertyInfo,
  setMapVisible,
}) => {
  const clientId = useClientId();
  const safeResponse = applyOverridesToResponse(
    specialtyPropertyInfo,
    clientId
  );
  const reportId = specialtyPropertyInfo.report_id;
  const sovs = specialtyPropertyInfo.report_json.sovs;
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const [editedColumnInfo, setEditedColumnInfo] =
    useState<SovColumnProps | null>(null);

  const updateReport = useUpdateReport(reportId);
  const updateAddresses = useUpdateAddresses(reportId);

  // Show enabled addresses
  const fullAddresses: RawAddressWithIndices[] = useMemo(() => {
    return (
      updateReport.isPending ? (updateReport.variables.sovs ?? sovs) : sovs
    )
      .filter((sov) => sov.is_enabled)
      .flatMap((sov) =>
        sov.addresses.map((address) => ({
          ...applyOverridesToAddress(address),
          __extras: {
            fullAddress: address,
          },
        }))
      );
  }, [sovs, updateReport.isPending, updateReport.variables?.sovs]);

  const unfilteredTotalTIV = useMemo(
    () => fullAddresses.reduce((total, address) => total + address.tiv, 0),
    [fullAddresses]
  );
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [columnModalVisible, setColumnModalVisible] = useState(false);
  const [bulkEditModalField, setBulkEditModalField] =
    useState<BulkEditModalState>(undefined);
  const logUIEvent = useLogUIEventCallback();

  const [editModalActiveIndex, setEditModalActiveIndex] = useState<
    number | undefined
  >(undefined);
  const [addAddressModalVisible, setAddAddressModalVisible] = useState(false);
  const { tabInfo } = useParams<{ tabInfo?: string }>();
  const navigate = useNavigate();
  const [streetViewModalVisible, setStreetViewModalVisible] =
    useState<StreetViewModalState>();
  const [reconciliationModalVisible, setReconciliationModalVisible] =
    useState(false);
  const currentUser = useLocalUser();
  const overflowMenuRef = useRef<Menu>(null);

  const hasAnyAddressOverrides = useMemo(
    () =>
      sovs.some((sov) =>
        sov.addresses.some(
          (a) => Object.keys(a.private_overrides).length > 0 || a.is_deleted
        )
      ),
    [sovs]
  );

  const clearAllAddressEdits = useCallback(() => {
    const accept = () => {
      const clearedAddresses: client.Address[] = [];
      for (const sov of sovs) {
        for (const address of sov.addresses) {
          if (
            Object.keys(address.private_overrides).length > 0 ||
            address.is_deleted
          ) {
            clearedAddresses.push({
              ...address,
              private_overrides: {},
              is_deleted: null,
            });
          }
        }
      }
      if (clearedAddresses.length > 0) {
        updateAddresses.mutate({ addresses: clearedAddresses });
      }
    };

    confirmDialog({
      message:
        "This will clear all manual edits on every address and un-delete any addresses that you have deleted. This cannot be undone.",
      header: "Clear All Address Edits",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Clear All",
      rejectLabel: "Cancel",
      accept,
    });
  }, [sovs, updateAddresses]);

  useEffect(() => {
    if (tabInfo) {
      if (tabInfo === "columns") {
        setColumnModalVisible(true);
      } else if (tabInfo === "construction") {
        setEditModalVisible(true);
        setEditModalActiveIndex(CONSTRUCTION_MODAL_INDEX);
      } else if (tabInfo === "occupancy" || tabInfo === "sheets") {
        setEditModalVisible(true);
        setEditModalActiveIndex(OCCUPANCY_MODAL_INDEX);
      }
      navigate(
        ROUTES.DASHBOARD_REPORT(specialtyPropertyInfo.report_id, "sov"),
        { replace: true }
      );
    }
  }, [tabInfo]);

  const onCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<RawAddressWithIndices>) => {
      if (!event.data || event.oldValue === event.newValue) return;

      const rowData = event.data;
      const oldAddress = rowData.__extras.fullAddress;

      // Build a RawAddress from the current grid row data (which now has the edited value)
      const { __extras, ...editedData } = rowData;
      void __extras;

      const { newAddress, fieldNameEdits } = applyDeltasAsOverride(
        oldAddress,
        editedData
      );
      logUIEvent("asgard_click_save_sov_edits", {
        field_name_edits: fieldNameEdits,
      });
      if (!isEqual(newAddress, oldAddress)) {
        updateAddresses.mutateAsync({ addresses: [newAddress] });
      }
    },
    [logUIEvent, updateAddresses]
  );

  const onDeleteAddress = useCallback(
    (address: RawAddressWithIndices) => {
      const accept = () => {
        const fullAddress = address.__extras.fullAddress;
        fullAddress.is_deleted = true;
        updateAddresses.mutate({ addresses: [fullAddress] });
      };

      confirmDialog({
        message: "Are you sure you want to delete this address?",
        header: "Delete Address",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        acceptLabel: "Delete",
        rejectLabel: "Cancel",
        accept,
      });
    },
    [updateAddresses]
  );

  const onCustomAction = useCallback(
    (action: CustomAction, data: RawAddressWithIndices) => () => {
      switch (action) {
        case "open_edit_modal_occupancy":
          setEditModalVisible(true);
          setEditModalActiveIndex(OCCUPANCY_MODAL_INDEX);
          break;
        case "open_edit_modal_construction":
          setEditModalVisible(true);
          setEditModalActiveIndex(CONSTRUCTION_MODAL_INDEX);
          break;
        case "open_street_view_modal":
          if (data.lat && data.long) {
            setStreetViewModalVisible({
              data,
            });
          }
          break;
      }
    },
    []
  );

  const locationCount = featureEnabled(
    clientId,
    "location-count-not-include-unique-buildings"
  )
    ? Object.keys(groupBy(fullAddresses, "premises_number")).length
    : fullAddresses.length;

  const addressColumns = getAddressColumns(specialtyPropertyInfo.lob);
  const isRerunningRemotely = useThisReportIsUpdating(safeResponse.report_id);
  const rerunningMessage = useRerunningMessage({
    update: isRerunningRemotely?.updating,
  });

  // Disable modal editors while a save is in-flight to avoid conflicting edits
  const disableModalEditors = updateAddresses.isPending;

  // ITV square footage helper
  const itvSquareFootage = useCallback((data: RawAddressWithIndices) => {
    const buildingValue = Number(data.building_value);
    if (Number.isNaN(buildingValue)) {
      return "";
    }
    const squareFootage = Number(data.square_footage);
    if (Number.isNaN(squareFootage) || squareFootage === 0) {
      return "";
    }
    return formatExactMoneyDollars(buildingValue / squareFootage);
  }, []);

  const isUpdatingAddress = useCallback(
    (data: RawAddressWithIndices) => {
      return (
        updateAddresses.isPending &&
        updateAddresses.variables.addresses.some(
          (a) => a.address_id === data.__extras.fullAddress.address_id
        )
      );
    },
    [updateAddresses.isPending, updateAddresses.variables]
  );

  const [quickFilterText, setQuickFilterText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Build AG Grid column definitions from the existing SovColumnProps.
  const columnDefs: ColDef<RawAddressWithIndices>[] = useMemo(() => {
    const cols: ColDef<RawAddressWithIndices>[] = [];

    // Delete action column (trash icon) + search trigger in header
    cols.push({
      colId: "actions-delete",
      headerName: "",
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      editable: false,
      headerComponent: () => (
        <div
          className={styles.searchIconHeader}
          onClick={() => {
            setSearchOpen((prev) => {
              if (prev) {
                setQuickFilterText("");
              }
              return !prev;
            });
          }}
        >
          <i
            className={searchOpen ? "pi pi-times" : "pi pi-search"}
            style={{ fontSize: "14px", cursor: "pointer" }}
          />
        </div>
      ),
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      cellRenderer: (params: { data: RawAddressWithIndices | undefined }) => {
        if (!params.data) return null;
        return (
          <Button
            onClick={() => onDeleteAddress(params.data!)}
            className={styles.buttonSmall}
            icon="pi pi-trash"
            severity="danger"
            text
            disabled={!!isRerunningRemotely}
            size="small"
            style={{
              position: "static",
            }}
          />
        );
      },
    });

    // Data columns from addressColumns config
    for (const columnInfo of addressColumns[clientId] ?? []) {
      const field = columnInfo.field;
      const isSortable = !columnInfo.isIconValue;

      const parsedWidth = columnInfo.width + AG_GRID_COL_PADDING;

      // For number editor columns whose underlying RawAddress field is a string
      // (e.g. building_value: string | null), we need a valueSetter that converts
      // the number back to a string so the row data stays consistent.
      const needsStringConversion =
        columnInfo.agEditor === "agNumberCellEditor" &&
        field &&
        field !== "tiv";

      const col: ColDef<RawAddressWithIndices> = {
        colId: field ?? columnInfo.columnName,
        field: field,
        headerName: columnInfo.columnName,
        width: parsedWidth,
        minWidth: parsedWidth,
        sortable: isSortable,
        comparator: naturalSortComparator,
        ...(columnInfo.filterable ? { filter: SetFilter } : {}),
        editable:
          !!columnInfo.agEditor &&
          !!field &&
          !isRerunningRemotely &&
          !updateAddresses.isPending,
        cellEditor: columnInfo.agEditor,
        cellEditorParams: columnInfo.agEditorParams,
        ...(columnInfo.isIconValue
          ? {
              cellStyle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }
          : {}),
        valueGetter: (params) => {
          if (!params.data) return undefined;
          if (columnInfo.columnName === "ITV") {
            return itvSquareFootage(params.data);
          }
          return columnInfo.getValue(params.data);
        },
        ...(needsStringConversion || field === "sic_code"
          ? {
              valueSetter: (params) => {
                if (!params.data || !field) return false;
                let newVal = params.newValue;
                // agSelectCellEditor passes formatted "CODE (Desc)" — extract just the code
                if (field === "sic_code" && typeof newVal === "string") {
                  newVal = newVal.split(" ")[0];
                }
                (params.data as Record<string, unknown>)[field] =
                  newVal == null || newVal === "" ? null : String(newVal);
                return true;
              },
            }
          : {}),
        cellRenderer: (params: {
          data: RawAddressWithIndices | undefined;
          value: unknown;
        }) => {
          if (!params.data) return null;
          const data = params.data;

          const effectiveColumnInfo =
            columnInfo.columnName === "ITV"
              ? { ...columnInfo, getValue: () => itvSquareFootage(data) }
              : columnInfo;

          return (
            <CellBody
              columnInfo={effectiveColumnInfo}
              fullAddress={data.__extras.fullAddress}
              isLightMode={isLightMode}
              isUpdatingAddress={isUpdatingAddress(data)}
              onClick={
                columnInfo.customAction
                  ? onCustomAction(columnInfo.customAction, data)
                  : undefined
              }
              isDisabled={false}
            />
          );
        },
        headerComponent: SovHeader,
        headerComponentParams: {
          columnInfo,
          isSortable,
          onBulkEdit: setEditedColumnInfo,
        },
      };

      cols.push(col);
    }

    return cols;
  }, [
    clientId,
    addressColumns,
    isLightMode,
    isRerunningRemotely,
    rerunningMessage,
    onCustomAction,
    onDeleteAddress,
    itvSquareFootage,
    isUpdatingAddress,
    updateAddresses.isPending,
    searchOpen,
  ]);

  const getRowId = useCallback(
    (params: { data: RawAddressWithIndices }) =>
      params.data.__extras.fullAddress.address_id,
    []
  );

  const gridRef = useRef<AgGridReact<RawAddressWithIndices>>(null);

  return (
    <Card
      title="Statement of Values"
      subTitle={
        <div className={styles.subtitleContainer}>
          <div className={styles.subtitleContentsContainerVertical}>
            <span>
              {"Total Insured Value: "}
              <b>{formatExactMoneyDollars(unfilteredTotalTIV)}</b>
            </span>
            <span>
              {"Location Count: "}
              <b>{locationCount}</b>
            </span>
          </div>
          <div className={styles.subtitleContentsContainer}>
            <EditSheets
              reportId={specialtyPropertyInfo.report_id}
              sovs={sovs}
              updateReport={updateReport}
            />
            {setMapVisible ? (
              <Button
                text
                raised
                severity="secondary"
                icon="pi pi-map"
                label="Show map"
                onClick={() => setMapVisible(true)}
              />
            ) : null}
            <Button
              text
              raised
              label="Const / Biz"
              severity="secondary"
              icon="pi pi-hammer"
              tooltip={
                disableModalEditors
                  ? "Please wait for changes to save before editing construction and business codes"
                  : "Review and edit construction codes and business information"
              }
              tooltipOptions={{ showDelay: 500, showOnDisabled: true }}
              onClick={() => setEditModalVisible(true)}
              disabled={disableModalEditors}
            />
            {sovs.some(
              (sov) => sov.column_mappings && sov.all_document_headers
            ) && (
              <Button
                text
                raised
                label="Columns"
                severity="secondary"
                icon="pi pi-arrow-right"
                onClick={() => setColumnModalVisible(true)}
              />
            )}
            <Button
              text
              raised
              label="Re-process"
              severity="danger"
              icon={
                isRerunningRemotely ? "pi pi-spin pi-spinner" : "pi pi-replay"
              }
              onClick={() => updateReport.mutate({ rerunReport: true })}
              disabled={isRerunningRemotely !== undefined}
            />
            <Menu
              model={(
                [
                  {
                    label: "Add Location",
                    icon: "pi pi-plus",
                    command: () => setAddAddressModalVisible(true),
                  },
                  {
                    label: "Clear Address Edits",
                    icon: "pi pi-eraser",
                    disabled: !hasAnyAddressOverrides,
                    command: clearAllAddressEdits,
                  },
                  featureEnabled(clientId, "tiv-reconciliation-functions", {
                    user: currentUser,
                  }) && sovs.some((sov) => sov.reconciliation_function)
                    ? {
                        label: "TIV Recon",
                        icon: "pi pi-code",
                        command: () => setReconciliationModalVisible(true),
                      }
                    : undefined,
                ] as (MenuItem | undefined)[]
              ).filter((item): item is MenuItem => item !== undefined)}
              popup
              ref={overflowMenuRef}
            />
            <Button
              icon="pi pi-ellipsis-v"
              onClick={(e) => overflowMenuRef.current?.toggle(e)}
              text
              raised
              severity="secondary"
              tooltip="More actions"
              tooltipOptions={{ showDelay: 500 }}
            />
          </div>
        </div>
      }
    >
      <div
        style={{ height: "70vh", width: "100%" }}
        className={styles.gridContainer}
      >
        {searchOpen && (
          <div className={styles.searchOverlay}>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                ref={searchInputRef}
                value={quickFilterText}
                onChange={(e) => setQuickFilterText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setQuickFilterText("");
                    setSearchOpen(false);
                  }
                }}
                placeholder="Search all columns..."
                style={{ width: "250px", paddingLeft: "2rem" }}
                size="small"
                autoFocus
              />
            </span>
          </div>
        )}
        <AgGridReact<RawAddressWithIndices>
          ref={gridRef}
          theme={isLightMode ? agThemeLight : agThemeDark}
          modules={[AllCommunityModule]}
          rowData={fullAddresses}
          columnDefs={columnDefs}
          getRowId={getRowId}
          onCellEditingStopped={onCellEditingStopped}
          headerHeight={60}
          rowHeight={36}
          alwaysShowHorizontalScroll={true}
          suppressHeaderFocus={true}
          quickFilterText={quickFilterText}
        />
      </div>
      <ColumnEditModal
        columnInfo={editedColumnInfo}
        visible={editedColumnInfo !== null}
        setVisible={() => setEditedColumnInfo(null)}
        fullAddresses={fullAddresses}
        onHide={() => setEditedColumnInfo(null)}
        updateAddresses={updateAddresses}
        onSaved={() => setEditedColumnInfo(null)}
      />
      <EditSovModal
        visible={editModalVisible}
        lob={specialtyPropertyInfo.lob}
        setVisible={setEditModalVisible}
        sovs={sovs}
        reportId={reportId}
        initialActiveIndex={editModalActiveIndex}
        rerunningMessage={rerunningMessage}
      />
      <ColumnMappingsModal
        visible={columnModalVisible}
        setVisible={setColumnModalVisible}
        sovs={sovs}
        reportId={reportId}
      />
      <BulkEditModal
        state={bulkEditModalField}
        onHide={() => setBulkEditModalField(undefined)}
        report={specialtyPropertyInfo.report_json}
        isLightMode={isLightMode}
      />
      {streetViewModalVisible ? (
        /* This component does not respond to "state" prop changes cleanly, so remount it every time to avoid stale state. */
        <StreetViewModal
          initialState={streetViewModalVisible}
          onHide={() => setStreetViewModalVisible(undefined)}
          isLightMode={isLightMode}
          reportId={reportId}
        />
      ) : null}
      <AddAddressModal
        visible={addAddressModalVisible}
        setVisible={setAddAddressModalVisible}
        additionalStyles={{}}
        lob={specialtyPropertyInfo.lob}
        report={safeResponse}
      />
      <ReconciliationFunctionModal
        visible={reconciliationModalVisible}
        setVisible={setReconciliationModalVisible}
        sovs={sovs}
      />
    </Card>
  );
};
